import React, { useId, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const CHART_WIDTH = 100;
const CHART_HEIGHT = 36;
const PAD = 4;

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const oneDecimalFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatDateKey = (date) => date.toISOString().slice(0, 10);

const isValidDateKey = (date) => {
  if(typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const parsedDate = new Date(`${date}T00:00:00.000Z`);
  return !Number.isNaN(parsedDate.getTime()) && formatDateKey(parsedDate) === date;
};

export const onDateToReport = ({
  onDate,
  key = 'temporal-report',
  title,
  units,
  valueLabel = 'Value',
  lookbackDays = 30,
  cumulative = true,
  endDate
}) => {
  const entries = Object.entries(onDate || {})
    .filter(([date]) => isValidDateKey(date))
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB));

  const resolvedEndDate = endDate && isValidDateKey(endDate)
    ? endDate
    : (entries[entries.length - 1]?.[0] || formatDateKey(new Date()));
  const endDateParsed = new Date(`${resolvedEndDate}T00:00:00.000Z`);
  const daysToInclude = Math.max(1, lookbackDays);
  const startDateParsed = new Date(endDateParsed);
  startDateParsed.setUTCDate(startDateParsed.getUTCDate() - (daysToInclude - 1));
  const startDate = formatDateKey(startDateParsed);

  const startingValue = cumulative
    ? entries.reduce((sum, [date, value]) => (
      date < startDate ? sum + toNumber(value) : sum
    ), 0)
    : 0;

  let currentValue = startingValue;
  const data = [...Array(daysToInclude)].map((_, i) => {
    const date = new Date(startDateParsed);
    date.setUTCDate(date.getUTCDate() + i);
    const dateKey = formatDateKey(date);
    currentValue = cumulative
      ? currentValue + toNumber(onDate?.[dateKey])
      : toNumber(onDate?.[dateKey]);
    return { date: dateKey, value: currentValue };
  });

  return {
    key,
    title,
    units,
    fieldDefinitions: [
      { name: 'date', label: 'Date', fieldType: 'date' },
      { name: 'value', label: valueLabel, fieldType: 'number' }
    ],
    result: {
      total: data[data.length - 1]?.value ?? 0,
      data,
      queryParams: {
        startDate,
        endDate: resolvedEndDate
      }
    }
  };
};

const formatValue = (value, fieldType, units) => {
  const formattedValue = numberFormatter.format(value);

  if (fieldType === 'currency') return `$${formattedValue}`;
  if (fieldType === 'percentage') return `${formattedValue}%`;
  return units ? `${formattedValue} ${units}` : formattedValue;
};

const formatSummaryMetricValue = (value, summaryMetric, fieldType, units) => {
  const absValue = Math.abs(value);
  let formattedValue;

  if (absValue >= 1000000) {
    formattedValue = `${oneDecimalFormatter.format(value / 1000000)}M`;
  } else if (absValue >= 1000) {
    formattedValue = `${oneDecimalFormatter.format(value / 1000)}K`;
  } else if (summaryMetric === 'average') {
    formattedValue = oneDecimalFormatter.format(value);
  } else {
    formattedValue = numberFormatter.format(value);
  }

  if (fieldType === 'currency') return `$${formattedValue}`;
  if (fieldType === 'percentage') return `${formattedValue}%`;
  return units ? `${formattedValue} ${units}` : formattedValue;
};

const smoothLinePath = (points, minY, maxY) => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1yRaw = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2yRaw = p2.y - (p3.y - p1.y) / 6;
    const cp1y = minY !== undefined && maxY !== undefined ? Math.min(maxY, Math.max(minY, cp1yRaw)) : cp1yRaw;
    const cp2y = minY !== undefined && maxY !== undefined ? Math.min(maxY, Math.max(minY, cp2yRaw)) : cp2yRaw;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

export const TemporalReportMini = ({
  report,
  summaryMetric,
  onClick,
  color = '#95a2d9',
  metricColor,
  metricSlotWidth = 25,
  className,
  style
}) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);
  const gradientId = `temporal-report-mini-${useId().replace(/:/g, '')}`;

  const fieldDefinitions = report?.fieldDefinitions?.length ? report.fieldDefinitions : [
    { name: 'date', label: 'Date', fieldType: 'date' },
    { name: 'value', label: 'Value', fieldType: 'number' }
  ];
  const xField = fieldDefinitions.find((field) => field.fieldType === 'date') || fieldDefinitions[0];
  const yField = fieldDefinitions.find((field) => field.fieldType === 'number') || fieldDefinitions[1];

  const rows = useMemo(
    () => (report?.result?.data || []).slice(),
    [report?.result?.data]
  );

  const chart = useMemo(() => {
    const rawPoints = rows.map((row, index) => {
      const rawX = xField?.fieldType === 'date' ? new Date(String(row[xField.name])).getTime() : Number(row[xField?.name]);
      return {
        x: Number.isFinite(rawX) ? rawX : index,
        y: toNumber(row[yField?.name || 'value']),
        row
      };
    });

    if (!rawPoints.length) {
      return {
        points: [],
        linePath: '',
        areaPath: '',
        zeroY: CHART_HEIGHT - PAD
      };
    }

    const xs = rawPoints.map((point) => point.x);
    const ys = rawPoints.map((point) => point.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(0, ...ys);
    const maxY = Math.max(0, ...ys);
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const width = CHART_WIDTH - (PAD * 2);
    const height = CHART_HEIGHT - (PAD * 2);
    const toSvg = (x, y) => ({
      svgX: PAD + ((x - minX) / rangeX) * width,
      svgY: PAD + height - ((y - minY) / rangeY) * height
    });
    const points = rawPoints.map((point) => ({ ...point, ...toSvg(point.x, point.y) }));
    const linePath = smoothLinePath(
      points.map((point) => ({ x: point.svgX, y: point.svgY })),
      PAD,
      CHART_HEIGHT - PAD
    );
    const first = points[0];
    const last = points[points.length - 1];
    const bottomY = CHART_HEIGHT - PAD;
    const areaPath = `${linePath} L ${last.svgX} ${bottomY} L ${first.svgX} ${bottomY} Z`;

    return {
      points,
      linePath,
      areaPath,
      zeroY: toSvg(minX, 0).svgY
    };
  }, [rows, xField, yField?.name]);

  if (!chart.points.length) return null;

  const values = rows.map((row) => toNumber(row[yField?.name || 'value']));
  const summaryMetricValue = summaryMetric === 'total'
    ? (typeof report?.result?.total === 'number' ? report.result.total : values.reduce((sum, value) => sum + value, 0))
    : summaryMetric === 'average'
      ? values.reduce((sum, value) => sum + value, 0) / values.length
      : null;

  const handleMouseMove = (event) => {
    if (!svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = rect.width > 0 ? CHART_WIDTH / rect.width : 1;
    const cursorSvgX = Math.max(PAD, Math.min(CHART_WIDTH - PAD, (event.clientX - rect.left) * scaleX));
    const activePoint = chart.points.reduce((closest, point) => (
      Math.abs(point.svgX - cursorSvgX) < Math.abs(closest.svgX - cursorSvgX) ? point : closest
    ));
    const label = String(activePoint.row[xField?.name || 'date'] ?? `Point ${chart.points.indexOf(activePoint) + 1}`);
    setTooltip({
      clientX: event.clientX,
      clientY: event.clientY,
      text: `${label}: ${formatValue(activePoint.y, yField?.fieldType, report?.units)}`,
      lineX: activePoint.svgX
    });
  };

  return (
    <Box
      className={className}
      style={style}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
      }}
    >
      {summaryMetric && summaryMetricValue !== null && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: CHART_WIDTH,
            minWidth: metricSlotWidth,
            mb: 0.25
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: metricColor || color,
              whiteSpace: 'nowrap'
            }}
          >
            {formatSummaryMetricValue(summaryMetricValue, summaryMetric, yField?.fieldType, report?.units)}
          </Typography>
        </Box>
      )}
      <svg
        ref={svgRef}
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
        onClick={onClick}
        style={{ display: 'block', cursor: onClick ? 'pointer' : 'default' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={chart.areaPath} fill={`url(#${gradientId})`} />
        {tooltip && (
          <line
            x1={tooltip.lineX}
            y1={PAD}
            x2={tooltip.lineX}
            y2={CHART_HEIGHT - PAD}
            stroke="rgba(0, 0, 0, 0.18)"
            strokeWidth={1}
          />
        )}
        <line
          x1={PAD}
          y1={chart.zeroY}
          x2={CHART_WIDTH - PAD}
          y2={chart.zeroY}
          stroke="rgba(0, 0, 0, 0.18)"
          strokeWidth={1}
        />
        <path
          d={chart.linePath}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {tooltip && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltip.clientX + 12,
            top: tooltip.clientY - 8,
            bgcolor: 'grey.800',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            typography: 'caption',
            pointerEvents: 'none',
            zIndex: 1
          }}
        >
          {tooltip.text}
        </Box>
      )}
    </Box>
  );
};
